import { z } from "zod";

/**
 * Vendors are the providers our team reaches out to (the admin calls them
 * "Partners"). Not to be confused with `partner_leads`, which are inbound
 * "Partner with us" form submissions.
 */
export const VENDOR_TYPES = [
  "restaurant",
  "spa",
  "activity",
  "pool",
  "show",
  "trip",
  "travel_agency",
  "stay",
  "park",
] as const;
export type VendorType = (typeof VENDOR_TYPES)[number];

/** Where a vendor is in our outreach. The channel lives on each contact. */
export const VENDOR_STAGES = [
  "to_contact",
  "contacted",
  "follow_up",
  "confirmed",
  "later",
  "no_reply",
  "refused",
] as const;
export type VendorStage = (typeof VENDOR_STAGES)[number];

/** The stages grouped the way the list page shows them: still going, won, closed. */
export const VENDOR_STAGE_GROUPS = {
  open: ["to_contact", "contacted", "follow_up"],
  won: ["confirmed"],
  closed: ["later", "no_reply", "refused"],
} as const satisfies Record<string, readonly VendorStage[]>;
export type VendorStageGroup = keyof typeof VENDOR_STAGE_GROUPS;

/** Stages where we are waiting on the venue, so a next follow-up date is expected. */
export const VENDOR_AWAITING_STAGES = ["contacted", "follow_up"] as const satisfies readonly VendorStage[];

export function stageAwaitsFollowUp(stage: VendorStage): boolean {
  return (VENDOR_AWAITING_STAGES as readonly VendorStage[]).includes(stage);
}

/** The "today" filters: follow-up overdue, due today, or a waiting partner with no date. */
export const VENDOR_FOLLOW_UP_WHEN = ["overdue", "today", "undated"] as const;
export type VendorFollowUpWhen = (typeof VENDOR_FOLLOW_UP_WHEN)[number];

export const VENDOR_CONTACT_CHANNELS = [
  "instagram",
  "whatsapp",
  "email",
  "call",
  "meeting",
] as const;
export type VendorContactChannel = (typeof VENDOR_CONTACT_CHANNELS)[number];

/**
 * Tags on a confirmed partner, replacing the sheet's unexplained colours and
 * side notes: "NO GOOGLE ADS" (Leopard, LouLou's), "Accepte virement"
 * (L'Istra), a trial deal (Anzar's first month).
 */
export const VENDOR_FLAGS = ["no_google_ads", "bank_transfer", "trial"] as const;
export type VendorFlag = (typeof VENDOR_FLAGS)[number];

/** Days until the next follow-up when a contact is logged (owner, 2026-10-16). */
export const VENDOR_FOLLOW_UP_DAYS = 3;

/**
 * The stage a vendor moves to when we log a message. The first message makes
 * it "contacted", the next one "follow_up". A decided stage (confirmed, later,
 * no reply, refused) is never changed by logging; the team sets those by hand.
 */
export function stageAfterContact(stage: VendorStage): VendorStage {
  if (stage === "to_contact") return "contacted";
  if (stage === "contacted") return "follow_up";
  return stage;
}

/** `YYYY-MM-DD` for the default follow-up, counted from `from`. */
export function defaultFollowUpDate(from: Date): string {
  const d = new Date(from.getTime());
  d.setUTCDate(d.getUTCDate() + VENDOR_FOLLOW_UP_DAYS);
  return d.toISOString().slice(0, 10);
}

const emptyToNull = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);
const emptyToUndefined = (v: unknown) =>
  typeof v === "string" && v.trim() === "" ? undefined : v;
const optionalText = (max: number) =>
  z.preprocess(emptyToNull, z.string().trim().max(max).nullable().optional());
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const vendorFields = z.object({
  name: z.string().trim().min(1).max(191),
  type: z.enum(VENDOR_TYPES),
  city: z.string().trim().min(1).max(120),
  instagramUrl: optionalText(500),
  websiteUrl: optionalText(500),
  googleReviews: z.preprocess(
    emptyToNull,
    z.coerce.number().int().min(0).nullable().optional(),
  ),
  contactName: optionalText(191),
  email: optionalText(191),
  phone: optionalText(64),
  stage: z.enum(VENDOR_STAGES),
  dealTerms: optionalText(500),
  notes: optionalText(5000),
  nextFollowUpAt: z.preprocess(emptyToNull, isoDay.nullable().optional()),
  flags: z.array(z.enum(VENDOR_FLAGS)).max(VENDOR_FLAGS.length).optional(),
});
/** A new partner starts at "to contact" unless the form says otherwise. */
export const vendorInputSchema = vendorFields.extend({
  stage: z.enum(VENDOR_STAGES).default("to_contact"),
});
export type VendorInput = z.infer<typeof vendorInputSchema>;

/**
 * Partial update: every field optional, same rules. Built from the fields
 * without the stage default: zod 4 applies a default even inside `.partial()`,
 * so a note or date saved from the list reset the stage to "to contact".
 */
export const vendorPatchSchema = vendorFields.partial();
export type VendorPatch = z.infer<typeof vendorPatchSchema>;

export const vendorContactInputSchema = z.object({
  channel: z.enum(VENDOR_CONTACT_CHANNELS),
  note: optionalText(2000),
  /** ISO timestamp; defaults to now on the server. */
  contactedAt: z.preprocess(emptyToUndefined, z.string().datetime().optional()),
  /** Stage after this contact; defaults to `stageAfterContact(current)`. */
  stage: z.enum(VENDOR_STAGES).optional(),
  /** `YYYY-MM-DD`, or null for none; defaults to 3 days after the contact. */
  nextFollowUpAt: z.preprocess(emptyToUndefined, isoDay.nullable().optional()),
});
export type VendorContactInput = z.infer<typeof vendorContactInputSchema>;

export const vendorsListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
  q: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
  type: z.preprocess(emptyToUndefined, z.enum(VENDOR_TYPES).optional()),
  stage: z.preprocess(emptyToUndefined, z.enum(VENDOR_STAGES).optional()),
  city: z.preprocess(emptyToUndefined, z.string().trim().max(120).optional()),
  /** "1" = next follow-up is today or earlier. */
  due: z.preprocess(emptyToUndefined, z.enum(["1"]).optional()),
  when: z.preprocess(emptyToUndefined, z.enum(VENDOR_FOLLOW_UP_WHEN).optional()),
});
export type VendorsListQuery = z.infer<typeof vendorsListQuerySchema>;

export interface VendorContactDTO {
  id: string;
  channel: VendorContactChannel;
  note: string | null;
  contactedAt: string;
  createdByName: string | null;
}

export interface VendorActivityLinkDTO {
  id: string;
  title: string;
  slug: string;
  /** The page's own commission: what a booking on it really charges. */
  commission: number;
  commissionType: "fixed" | "percent";
}

export interface VendorDTO {
  id: string;
  name: string;
  type: VendorType;
  city: string;
  instagramUrl: string | null;
  websiteUrl: string | null;
  googleReviews: number | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  stage: VendorStage;
  dealTerms: string | null;
  notes: string | null;
  nextFollowUpAt: string | null;
  lastContactedAt: string | null;
  flags: VendorFlag[];
  createdAt: string;
  updatedAt: string;
}

export interface VendorDetailDTO extends VendorDTO {
  contacts: VendorContactDTO[];
  activities: VendorActivityLinkDTO[];
}

/** Counts per stage plus the follow-up buckets, for the list page header. */
export type VendorStageCounts = Record<VendorStage, number> &
  Record<VendorFollowUpWhen, number> & { due: number; all: number };

/** A confirmed partner with its catalogue pages, for the Confirmed tab. */
export interface ConfirmedVendorDTO extends VendorDTO {
  pages: VendorActivityLinkDTO[];
}

/**
 * Our cut from a partner, read from its pages (commission lives on the
 * activity, owner 2026-10-16), so the tab can never disagree with what a
 * booking charges. `varies` when its pages charge different cuts, `zero`
 * when every page is at 0, `none` when no page is linked yet.
 */
export type VendorCut =
  | { kind: "percent" | "fixed"; value: number }
  | { kind: "varies" }
  | { kind: "zero" }
  | { kind: "none" };

export function vendorCut(pages: readonly Pick<VendorActivityLinkDTO, "commission" | "commissionType">[]): VendorCut {
  if (pages.length === 0) return { kind: "none" };
  if (pages.every((p) => p.commission <= 0)) return { kind: "zero" };
  const [first] = pages;
  const same = pages.every(
    (p) => p.commission === first!.commission && p.commissionType === first!.commissionType,
  );
  if (!same) return { kind: "varies" };
  return { kind: first!.commissionType === "percent" ? "percent" : "fixed", value: first!.commission };
}

/** The Confirmed tab's gap tiles: partners missing a cut, or missing a page. */
export const CONFIRMED_GAPS = ["no_cut", "no_page"] as const;
export type ConfirmedGap = (typeof CONFIRMED_GAPS)[number];

export function confirmedGap(pages: ConfirmedVendorDTO["pages"]): ConfirmedGap | null {
  const cut = vendorCut(pages);
  if (cut.kind === "none") return "no_page";
  if (cut.kind === "zero") return "no_cut";
  return null;
}

/**
 * How many partners each type and each city chip would show on the Outreach
 * tab: every list filter applies except the row's own (types are counted
 * under the picked city, cities under the picked type).
 */
export interface VendorFacets {
  types: Record<string, number>;
  typesAll: number;
  cities: Record<string, number>;
  citiesAll: number;
}
